import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { ArrowRight } from 'lucide-react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { OPEN_CREATE_PCR_DIALOG } from '../constants/userConstants'
import SEO from '../components/SEO'
import './LandingPage.css'

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const scrollToId = (id) => {
  const el = document.getElementById(id)
  if (!el) return
  el.scrollIntoView({
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    block: 'start',
  })
}

// The five pipeline stages. Content is the product's own sample output.
const STAGES = [
  {
    n: '1.0',
    label: 'Narrative',
    note: 'Type or dictate the call the way you would say it out loud.',
    content:
      'Dispatched to private residence for 65 y/o male c/o chest pain and dizziness. ' +
      'Upon arrival, patient found sitting upright on couch, alert and oriented x3. ' +
      'Patient reports onset of substernal chest pressure approximately 45 minutes prior, radiating to left arm. ' +
      'Denies shortness of breath, nausea, or recent trauma. ' +
      'Vitals: BP 148/92, HR 88, RR 18, SpO2 97% on room air. ' +
      'Lung sounds clear bilaterally, no JVD, no pedal edema noted. ' +
      '12-lead EKG obtained, showing ST depression in lateral leads. ' +
      'Aspirin 324 mg administered PO per protocol, IV established in left AC with NS TKO. ' +
      'Patient transported emergent to local cardiac center with continuous monitoring en route. ',
  },
  {
    n: '2.0',
    label: 'Format',
    note: 'The narrative is restructured into the report type you chose — CHART, SOAP, or DCHART.',
    content:
      'Chief Complaint: Dispatched to a private residence for a 65-year-old male complaining of chest pain and dizziness.\n\n' +
      'History: Upon arrival, the patient was found sitting upright on a couch. The patient reported the onset of substernal chest pressure approximately 45 minutes prior. The pain was radiating to the left arm. He denied shortness of breath. He denied nausea. He denied recent trauma.\n\n' +
      'Assessment: The patient was alert and oriented x3. Vitals: BP 148/92, HR 88, RR 18, SpO2 97% on room air. Lung sounds were clear bilaterally. No jugular venous distention was noted. No pedal edema was noted. The EKG showed ST depression in the lateral leads.\n\n' +
      'Rx/Treatment: A 12-lead EKG was obtained. Aspirin 324 mg was administered orally per protocol. An IV was established in the left antecubital with normal saline to keep open.\n\n' +
      'Transport: The patient was transported emergently to the local cardiac center. Continuous monitoring was provided en route.',
  },
  {
    n: '3.0',
    label: 'Template',
    note: 'Your service template is merged in. Where the two conflict, your narrative wins.',
    content:
      'Chief Complaint: Dispatched to a private residence for a 65-year-old male complaining of chest pain and dizziness.\n\n' +
      'History: Patient reported the onset of substernal chest pressure approximately 45 minutes prior, radiating to the left arm. Patient denied shortness of breath, nausea, or recent trauma. ' +
      'Patient denied use of illicit drugs or alcohol. No language barrier existed between patient and providers. No change in medications recently.\n\n' +
      'Assessment: Patient was awake, alert and oriented x3. Patient denied loss of consciousness. Airway was patent. Pt. was speaking without difficulty and had no complaints indicating airway obstruction. ' +
      'Patient had non-labored respirations with a rate of 18, no accessory muscle use. Lung sounds were clear bilaterally. Radial pulses were present; pulses were strong and regular. Heart rate was 88. ' +
      'Patient was sober and competent. No odor on breath noted. Skin was pink, warm and dry with strong radial pulses. Patient could speak in full sentences, no facial droop noted. ' +
      'No blood and/or clear fluid leaking from nares or ear canals. Palpation and visualization of the head, eyes, ears, nose, mouth, and throat revealed no contusions, abrasions, deformities or tenderness. ' +
      'Neck mid-line and intact without pain on palpation or movement. No jugular venous distention noted. Thoracic, lumbar and sacral spine intact without pain, deformities or step-offs on palpation or spontaneous movement. ' +
      'No pain in sacral or coccygeal region. Chest was atraumatic with equal rise and fall. No pain upon palpation or flail segments, with no urticaria nor deformities observed. ' +
      "Patient's abdomen was soft, non-tender, a-traumatic and unremarkable with no distention or rigidity on palpation and visualization to all quadrants. DCAP-BTLS was absent and there were no pulsatile masses. " +
      "Patient's pelvis was sturdy, intact with no pain on palpation anterior or lateral. All extremities were intact with no DCAP-BTLS noted. No pedal edema noted. Pulses, sensation and movement were present in each extremity. " +
      'Vitals: BP 148/92, HR 88, RR 18, SpO2 97% on room air. Patient denied headache, blurred vision, shortness of breath, numbness or tingling in extremities and localized paralysis. ' +
      'Patient had no distracting injuries. A 12-lead EKG was obtained, showing ST depression in lateral leads.\n\n' +
      'Rx/Treatment: Aspirin 324 mg was administered orally per protocol. An IV was established in the left antecubital with normal saline to keep open.\n\n' +
      'Transport: Patient transport required for evaluation. The patient was transported emergently to a local cardiac center with continuous monitoring en route. The condition remained stable during transport. The patient was secured with seatbelts across the chest, waist and legs (6 point safety straps) and secured for transport. While en route the patient was reassessed and vitals taken. Upon arrival, a report was given to the emergency department staff without incident. Patient care was acknowledged and transferred to the attending ED staff. Prior to departure both the patient and attending RN signatures were obtained, along with a facesheet. Patient was transferred to the hospital bed via draw sheet lift without incident. Patient was left in hospital bed with rails up and staff in attendance. No incidents or injuries occurred during this call.',
  },
  {
    n: '4.0',
    label: 'Concise',
    note: 'Standard abbreviations are applied so the report reads the way charts are written.',
    content:
      'Chief Complaint: Dispatched to a private residence for a 65 y/o M c/o CP and dizziness.\n\n' +
      'History: Pt reported the onset of substernal CP approx 45 min prior, radiating to the L arm. Pt denied SOB, N/V, or recent trauma. Pt denied use of illicit drugs or ETOH. No language barrier existed between pt and providers. No change in meds recently.\n\n' +
      "Assessment: Pt was AAOx3. Pt denied LOC. Airway was patent. Pt was speaking w/o difficulty and had no c/o indicating airway obstruction. Pt had non-labored resps with a rate of 18, no accessory muscle use. BS were clear bilat. Radial pulses were present; pulses were strong and regular. HR was 88. Pt was sober and competent. No odor on breath noted. Skin was pink, warm and dry with strong radial pulses. Pt could speak in full sentences, no facial droop noted. No blood and/or clear fluid leaking from nares or ear canals. Palpation and visualization of the HEENT revealed no DCAP-BTLS. Neck mid-line and intact w/o pain on palpation or movement. No JVD noted. T/L/S spine intact w/o pain, deformities or step-offs on palpation or spontaneous movement. No pain in sacral or coccygeal region. Chest was atraumatic with equal rise and fall. No pain upon palpation or flail segments, with no urticaria nor deformities observed. Pt's abd was soft, non-tender, a-traumatic and unremarkable with no distention or rigidity on palpation and visualization to all quadrants. DCAP-BTLS was absent and there were no pulsatile masses. Pt's pelvis was sturdy, intact with no pain on palpation anterior or lateral. All extremities were intact with no DCAP-BTLS noted. No pedal edema noted. PMS were present in each extremity. VS: BP 148/92, HR 88, RR 18, SpO2 97% on RA. Pt denied HA, blurred vision, SOB, numbness or tingling in extremities and localized paralysis. Pt had no distracting injuries. A 12-lead ECG was obtained, showing ST depression in lateral leads.\n\n" +
      'Rx/Treatment: ASA 324 mg was administered PO per protocol. An IV was established in the L AC with NS TKO.\n\n' +
      'Transport: Pt transport required for eval. The pt was transported emergently to a local cardiac center with continuous monitoring en route. The condition remained stable during transport. The pt was secured with seatbelts across the chest, waist and legs (6 point safety straps) and secured for transport. While en route the pt was reassessed and VS taken. Upon arrival, a report was given to the ED staff w/o incident. Pt care was acknowledged and transferred to the attending ED staff. Prior to departure both the pt and attending RN signatures were obtained, along with a facesheet. Pt was transferred to the hospital bed via draw sheet lift w/o incident. Pt was left in hospital bed with rails up and staff in attendance. No incidents or injuries occurred during this call.',
  },
  {
    n: '5.0',
    label: 'Review',
    note: 'A review pass names what is missing or inconsistent. You decide what to act on.',
    content:
      'Chief Complaint: The Chief Complaint section is clear, stating the primary reason for the call as chest pain (CP) and dizziness in a 65-year-old male. No additional feedback needed.\n\n' +
      "History: The History section is mostly comprehensive, detailing the onset and nature of the chest pain, as well as relevant negatives such as denial of shortness of breath (SOB), nausea/vomiting (N/V), and recent trauma. However, it could benefit from additional information on the patient's past medical history, current medications, and any known allergies to provide a fuller picture.\n\n" +
      "Assessment: The Assessment section is thorough, covering vital signs, physical examination findings, and a 12-lead ECG result. However, it would be helpful to include the patient's temperature and any relevant past cardiac history or risk factors for a more complete assessment.\n\n" +
      "Rx/Treatment: The Rx/Treatment section is clear about the administration of aspirin and the establishment of an IV line. However, it lacks information on the patient's response to the treatment provided, which is crucial for evaluating the effectiveness of the interventions.\n\n" +
      "Transport: The Transport section is detailed, describing the patient's condition during transport, the destination, and the handoff process. However, it could include more specific information about any changes in the patient's condition during transport and any additional interventions or reassessments performed.\n\n" +
      "Overall: Overall, the report is detailed and covers most necessary aspects of the patient encounter. To enhance clarity and completeness, it should include more information on the patient's medical history, allergies, and response to treatment. Additionally, documenting any changes in condition during transport would provide a more comprehensive view of the patient's journey from the scene to the hospital.",
  },
]

const CAPABILITIES = [
  {
    term: 'Report formats',
    desc: 'One narrative becomes a CHART, SOAP, or DCHART report. You pick the format when you start the report.',
  },
  {
    term: 'Template merge',
    desc: 'Your service’s template is merged into the narrative. Where the two conflict, what you wrote supersedes the template text.',
  },
  {
    term: 'Medical concision',
    desc: 'Standard abbreviations are applied — AAOx3, SOB, DCAP-BTLS, PMS — so the report reads the way charts are written.',
  },
  {
    term: 'Review pass',
    desc: 'The report is checked for inconsistencies and missing sections, and told back to you as notes rather than silent edits.',
  },
  {
    term: 'Voice entry',
    desc: 'Dictate the narrative instead of typing it, for when your hands are still busy.',
  },
  {
    term: 'Protocol questions',
    desc: 'Ask questions against your own protocol document and get the answer with the section it came from.',
  },
]

const DATA_HANDLING = [
  { term: 'Nothing is stored', desc: 'No report content is retained on our systems after the request completes.' },
  { term: 'Encrypted in transit', desc: 'All traffic is encrypted in line with HIPAA requirements.' },
  { term: 'You are the author', desc: 'Every report comes back as a draft for you to review, edit, and sign.' },
]

const LandingPage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const rootRef = useRef(null)
  const tabRefs = useRef([])

  const [stage, setStage] = useState(0)
  const [hipaaOpen, setHipaaOpen] = useState(false)

  // One orchestrated reveal. Reduced motion opts out entirely.
  useEffect(() => {
    const nodes = rootRef.current?.querySelectorAll('.lp__reveal')
    if (!nodes?.length) return

    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => n.classList.add('is-in'))
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('is-in')
          observer.unobserve(entry.target)
        })
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    )

    nodes.forEach((n) => observer.observe(n))
    return () => observer.disconnect()
  }, [])

  // '#demo' is kept as an alias — the section was called that before the redesign.
  useEffect(() => {
    const hash = window.location.hash
    if (hash === '#stages' || hash === '#demo') {
      const t = setTimeout(() => scrollToId('stages'), 100)
      return () => clearTimeout(t)
    }
  }, [])

  const selectStage = useCallback((index, { moveFocus = false } = {}) => {
    setStage(index)
    if (moveFocus) {
      // preventScroll keeps arrow-key navigation from jumping the page (gate 53).
      tabRefs.current[index]?.focus({ preventScroll: true })
    }
  }, [])

  const onTabKeyDown = (event) => {
    const last = STAGES.length - 1
    let next = null

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = stage === last ? 0 : stage + 1
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = stage === 0 ? last : stage - 1
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = last

    if (next === null) return
    event.preventDefault()
    selectStage(next, { moveFocus: true })
  }

  const current = STAGES[stage]

  return (
    <>
      <SEO
        title="PCRAssist — AI-assisted patient care reports for EMS"
        description="PCRAssist turns an EMS field narrative into a CHART, SOAP, or DCHART patient care report — merged with your service template, checked for gaps, and returned as a draft you review and sign."
        canonicalUrl="/"
      />

      <div className="lp" ref={rootRef}>
        {/* ---- notice ------------------------------------------------- */}
        <div className="lp__notice">
          <div className="lp__wrap lp__notice-inner">
            <span className="lp__notice-tag">New</span>
            <p>
              Ask questions against your own protocol document.{' '}
              <Link className="lp__link" to="/protocol">
                Open Protocol Q&amp;A
              </Link>
            </p>
          </div>
        </div>

        {/* ---- hero — H2 split diptych -------------------------------- */}
        <header className="lp__hero">
          <div className="lp__wrap lp__hero-grid">
            <div className="lp__hero-copy lp__reveal">
              <h1>
                Write the narrative.<br />
                <b>Not the format.</b>
              </h1>

              <p className="lp__lede">
                PCRAssist turns your field narrative into a CHART, SOAP, or DCHART report — merged with
                your service template, checked for gaps, and handed back as a draft. You stay the author.
              </p>

              <div className="lp__ctas">
                <button
                  type="button"
                  className="lp__btn lp__btn--primary"
                  onClick={() => dispatch({ type: OPEN_CREATE_PCR_DIALOG })}
                >
                  Start a report
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="lp__btn lp__btn--secondary"
                  onClick={() => scrollToId('stages')}
                >
                  See a worked example
                </button>
              </div>

              <div className="lp__hero-meta">
                <span className="lp__label">Built for EMS crews</span>
                <span className="lp__label">CHART · SOAP · DCHART</span>
              </div>
            </div>

            {/* Typographic frame — no re-drawn window chrome (gate 47). */}
            <figure className="lp__card lp__reveal" aria-labelledby="lp-card-cap">
              <div className="lp__card-bar">
                <figcaption className="lp__label" id="lp-card-cap">
                  Sample · chest pain
                </figcaption>
                <span className="lp__chip">CHART</span>
              </div>
              <div className="lp__card-body">
                <div className="lp__card-in">
                  <span className="lp__label">You write</span>
                  <p>
                    65 y/o male c/o chest pain, onset 45 min, radiating to L arm. BP 148/92, HR 88.
                    ASA 324 PO. Txp emergent.
                  </p>
                </div>
                <hr className="lp__card-div" />
                <div className="lp__card-out">
                  <span className="lp__label">You get back</span>
                  <p>
                    <b>Chief Complaint:</b> 65-year-old male complaining of chest pain.{'\n'}
                    <b>History:</b> Onset of substernal chest pressure approximately 45 minutes prior,
                    radiating to the left arm.{'\n'}
                    <b>Rx/Treatment:</b> Aspirin 324 mg administered orally per protocol.{'\n'}
                    <b>Transport:</b> Emergent transport.
                  </p>
                </div>
              </div>
            </figure>
          </div>
        </header>

        {/* ---- stages — the workflow spine ---------------------------- */}
        <section className="lp__stages" id="stages" aria-labelledby="lp-stages-title">
          <div className="lp__wrap">
            <div className="lp__head lp__stages-head lp__reveal">
              <h2 id="lp-stages-title">AI as an assistant — not an author</h2>
              <p>
                Five stages run between the narrative you type and the report you sign. Step through them
                on a real chest-pain call.
              </p>
            </div>

            <div className="lp__reveal">
              <div className="lp__tabs" role="tablist" aria-label="Report stages" onKeyDown={onTabKeyDown}>
                {STAGES.map((s, i) => (
                  <button
                    key={s.n}
                    type="button"
                    role="tab"
                    id={`lp-tab-${i}`}
                    aria-selected={i === stage}
                    aria-controls="lp-stage-panel"
                    tabIndex={i === stage ? 0 : -1}
                    data-done={i < stage}
                    className="lp__tab"
                    ref={(el) => {
                      tabRefs.current[i] = el
                    }}
                    onClick={() => selectStage(i)}
                  >
                    <span className="lp__tab-n">{s.n}</span>
                    {s.label}
                  </button>
                ))}
              </div>

              <div
                className="lp__panel"
                role="tabpanel"
                id="lp-stage-panel"
                aria-labelledby={`lp-tab-${stage}`}
                tabIndex={0}
              >
                <div className="lp__panel-bar">
                  <span className="lp__label">
                    {current.n} · {current.label}
                  </span>
                  <p className="lp__panel-note">{current.note}</p>
                </div>
                <div className="lp__panel-body">{current.content}</div>
              </div>

              <div className="lp__stage-nav">
                <button
                  type="button"
                  className="lp__btn lp__btn--secondary"
                  onClick={() => selectStage(stage - 1)}
                  disabled={stage === 0}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="lp__btn lp__btn--secondary"
                  onClick={() => selectStage(stage + 1)}
                  disabled={stage === STAGES.length - 1}
                >
                  Next stage
                </button>
                <span className="lp__stage-count">
                  Stage {stage + 1} of {STAGES.length}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ---- capabilities — F3 spec sheet --------------------------- */}
        <section className="lp__spec" aria-labelledby="lp-spec-title">
          <div className="lp__wrap">
            <div className="lp__head lp__spec-head lp__reveal">
              <h2 id="lp-spec-title">What it does</h2>
            </div>

            <dl className="lp__spec-list lp__reveal">
              {CAPABILITIES.map((c) => (
                <div className="lp__spec-row" key={c.term}>
                  <dt>{c.term}</dt>
                  <dd>{c.desc}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* ---- data handling — the one graphite band ------------------ */}
        <section className="lp__wrap" aria-labelledby="lp-band-title">
          <div className="lp__band lp__reveal">
            <span className="lp__label">Data handling</span>
            <h2 id="lp-band-title">Patient data does not stay with us</h2>

            <div className="lp__band-grid">
              {DATA_HANDLING.map((d) => (
                <div className="lp__band-item" key={d.term}>
                  <b>{d.term}</b>
                  <span>{d.desc}</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="lp__btn lp__btn--secondary"
              onClick={() => setHipaaOpen(true)}
            >
              Read the HIPAA summary
            </button>
          </div>
        </section>

        {/* ---- close — one CTA ---------------------------------------- */}
        <section className="lp__wrap lp__close lp__reveal" aria-labelledby="lp-close-title">
          <h2 id="lp-close-title">Start with the call you just ran.</h2>
          <div className="lp__ctas">
            <button
              type="button"
              className="lp__btn lp__btn--primary"
              onClick={() => dispatch({ type: OPEN_CREATE_PCR_DIALOG })}
            >
              Start a report
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>
          <span className="lp__label">
            Or{' '}
            <button type="button" className="lp__linkbtn" onClick={() => navigate('/protocol')}>
              ask your protocol a question
            </button>
          </span>
        </section>

        {/* ---- HIPAA dialog ------------------------------------------- */}
        <Dialog open={hipaaOpen} onClose={() => setHipaaOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle className="lp-dialog__title">HIPAA compliance</DialogTitle>
          <DialogContent className="lp-dialog__body" dividers>
            <p>
              No report data is ever stored on our systems, and all traffic is encrypted according to
              HIPAA regulations.
            </p>
            <p>
              We are committed to maintaining the highest standards of data security and privacy for
              healthcare professionals. All communications are secured with end-to-end encryption, and we
              follow strict HIPAA compliance protocols.
            </p>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation onClick={() => setHipaaOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  )
}

export default LandingPage
